import { DomSanitizer } from '@angular/platform-browser';
import { System } from './../System';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { detailExpand } from './../angular-material/mat-animations';
import { Topic } from './topic';
import { Component, OnInit, Input, OnChanges, EventEmitter, Output, ChangeDetectorRef } from '@angular/core';
import { TylerMatTableDataSource } from '../angular-material/extended.class';
import { Profile } from './profile.model';
import { ProfileNode } from './profile-node';
import { EditTopicDialogComponent } from './edit-topic-dialog/edit-topic-dialog.component';
import { DeleteTopicDialogComponent } from './delete-topic-dialog/delete-topic-dialog.component';
import { AddEditContentDialogComponent } from './add-edit-content-dialog/add-edit-content-dialog.component';
import { AttachmentsMenuDialogComponent } from './attachments-menu-dialog/attachments-menu-dialog.component';
import { LoginService } from '../../shared-modules/login/login.service';
import { UserService } from '../user/user.service';
import { UserType } from '../user/user-type';

/**
 * This component is used for creating a table of profiles.
 *
 * @input profiles: a list of profiles to display
 * @input displayInactive: set this to true if you want to display inactive profiles. False by default.
 * @input userCanEdit: set this to true if the user can edit the profiles. False by default.
 * @input dataChanged: this is an event emitter signalling if the profiles have changed.
 *      If your profiles might change, be sure to supply this event emitter.
 *      Emit from that event emitter if and when your profiles change.
 * @input expandCollapseEvent: an event emitter that emits booleans.
 *      When this emits true, all the rows will expand.
 *      When this emits false, all the rows will collapse.
 * @input isViewing: a boolean indicating whether this is a person simply viewing the profiles (in the case of S360). False by default.
 * @input borderBottom: whether the rows should have a bottom border. Defaults to true.
 * @input enforceDates: whether it removes profiles whose parent dates are passed
 * @output unsavedChanges: an event emitter that fires true when there are unsaved changes and false when there are no longer.
 * @output reload: an event emitter that fires true when saved the changes.
 */
@Component({
    selector: 'profile-table',
    templateUrl: './profile-table.component.html',
    styleUrls: [
        './profile-table.component.css'
    ],
    animations: [
        detailExpand
    ]
})
export class ProfileTableComponent implements OnInit, OnChanges {

    // Properties
    @Input() profiles: Profile[] = [];
    @Input() displayInactive = false;
    @Input() userCanEdit = false;
    @Input() dataChanged: EventEmitter<any>;
    @Input() expandCollapseEvent: EventEmitter<boolean>;
    @Input() isViewing = false;
    @Input() borderBottom = true;
    @Input() enforceDates = false;
    @Output() unsavedChanges = new EventEmitter<boolean>();
    @Output() reload = new EventEmitter<boolean>();
    topics: Topic[];
    tableData: TylerMatTableDataSource;
    openInformation: Profile[];
    topicColumns = ['more', 'topic', 'inactive', 'actions'];
    profileColumns = ['more', 'content title', 'inactive', 'start date', 'end date', 'attachments', 'actions', 'attachments link'];
    columnWidths: {[key: string]: number} = {
        'more': 60,
        'content title': 100,
        'inactive': 60,
        'start date': 100,
        'end date': 100,
        'attachments': 60,
        'actions': 60,
        'attachments link': 100
    };
    allColumns = [this.topicColumns, this.profileColumns];
    junkDate = new Date('Wed Dec 31 1969 19:00:00 GMT-0500 (Eastern Standard Time)');
    selectedRow: Topic;
    subscriptions: Subscription[] = [];
    profileTableWidth = 0;
    V10= false;
    isExpandAll = false;
    isStaff = false;

    // Class Functions
    constructor (
        private dialog: MatDialog,
        private sanitizer: DomSanitizer,
        private changeDetector: ChangeDetectorRef,
        private loginService: LoginService,
        private userService: UserService
    ) {}

    // Event Functions
    ngOnInit() {
        // PRE: profiles supplied by the caller
        // POST: constructs the table data
        this.V10 = this.loginService.LoginSettings.V10;
        this.isStaff = this.userService.currentUser.UserType == UserType.Staff;
        this.unsavedChanges.emit(false);
        this.assembleDisplayData();
        if (this.dataChanged) {
            this.subscriptions.push(
                this.dataChanged.subscribe(() => {
                    this.assembleDisplayData();
                }),
            );
        }
        if (this.expandCollapseEvent) {
            this.subscriptions.push(
                this.expandCollapseEvent.subscribe(expand => {
                    this.toggleExpandCollapse(expand);
                })
            );
        }
    }

    expandAll() {
        this.isExpandAll = !this.isExpandAll;
        this.toggleExpandCollapse(this.isExpandAll);
    }

    private toggleExpandCollapse(expand: boolean) {
        if (this.topics) {
            this.topics.forEach(topic => {
                if (topic.subLevelNodes.length > 0 && this.tableData.checkExpanded(topic) != expand) {
                    this.tableData.toggleExpanded(topic);
                }
                topic.children.forEach(child => {
                    if (child.children.length > 0 && child.parent && child.parent.subTableData.checkExpanded(child) != expand) {
                        child.parent.subTableData.toggleExpanded(child);
                    }
                });
            });
        }
    }

    ngOnChanges() {
        // PRE: the profiles are changed
        // POST; constructs the table data
        this.assembleDisplayData();
    }

    // Methods
    resourceSrc(node: ProfileNode) {
        // POST: auto-trusts the data we are given for the image
        return this.sanitizer.bypassSecurityTrustResourceUrl(
            'data: image/jpeg;' +
            'base64,' + node.data.Image
        );
    }

    editTopic(information: Profile) {
        // POST: shows the edit topic dialog
        const dialogBox = this.dialog.open(EditTopicDialogComponent, {
            data: {
                information: information
            },
            disableClose: true
        });
        this.subscriptions.push(
            dialogBox.afterClosed().subscribe(() => {
                if (dialogBox.componentInstance.saved) {
                    this.reload.emit(true);
                }
            })
        );
    }

    deleteTopic(information: Profile) {
        // POST: shows the delet topic dialog
        const dialogBox = this.dialog.open(DeleteTopicDialogComponent, {
            data: {
                information: information
            },
            disableClose: true
        });
        this.subscriptions.push(
            dialogBox.afterClosed().subscribe(() => {
                if (dialogBox.componentInstance.deleted) {

                    // Removing the node and it's children
                    this.profiles.splice(this.profiles.indexOf(information), 1);
                    for (let i = 0; i < this.profiles.length; i++) {

                        // If I deleted a topic node, delete all its sub level nodes
                        if (information.ParentID == -2) {
                            if (information.TopicId == this.profiles[i].TopicId) {
                                this.profiles.splice(i, 1);
                                i -= 1;
                            }
                        }

                        // Otherwise, delete it if it matches it's parent id
                        else if (information.ProfileContentId == this.profiles[i].ParentID) {
                            this.profiles.splice(i, 1);
                            i -= 1;
                        }
                    }

                    // Reassembling the table
                    this.assembleDisplayData();

                    // Reload the main screen
                    this.reload.emit(true);
                }
            })
        );
    }

    addContent(parentNode: ProfileNode) {
        // POST: shows the dialog for adding content
        let prof: Profile[];
        let sequence  = 0; 
        if (!this.V10) {
            if (parentNode.data.ParentID == -2) {
                prof = this.profiles.filter(x => x.TopicId == parentNode.data.TopicId && x.ParentID != -2);
                sequence = prof ? Math.max.apply(null, prof.map(info => info.sequence)) + 1 : 0;
            }
            else {
                prof = this.profiles.filter(x => x.ParentID == parentNode.data.ProfileContentId);
                sequence = prof ?  Math.max.apply(null, prof.map(info => info.sequence)) + 1 : 0;
            }
        }
        else {
            sequence =  Math.max.apply(null, this.profiles.map(info => info.ParentID == parentNode.data.ProfileContentId ? info.sequence : -1)) + 1;
        }
        
        //var count = Math.max.apply(null, this.profiles.map(info => info.TopicId == parentNode.data.TopicId && info.ParentID == parentNode.data.ProfileContentId ? (info.sequence == -Infinity ? -1 : info.sequence) : -1)) + 1;
        const dialogBox = this.dialog.open(AddEditContentDialogComponent, {
            data: {
                rowId: Math.max.apply(null, this.profiles.map(info => info.RowId)) + 1,
                sequence: sequence,
                parentId: parentNode.data.ProfileContentId,
                topicId: parentNode.data.TopicId,
                userCanEdit: this.userCanEdit
            },
            disableClose: true
        });
        this.subscriptions.push(
            dialogBox.afterClosed().subscribe(() => {
                if (dialogBox.componentInstance.saved) {
                    this.profiles.push(dialogBox.componentInstance.information);
                    this.assembleDisplayData();
                }
            })
        );
    }

    editContent(information: Profile) {
        // POST: shows the dialog for editing content
        const dialogBox = this.dialog.open(AddEditContentDialogComponent, {
            data: {
                information: information,
                userCanEdit: this.userCanEdit
            },
            disableClose: true
        });
        this.subscriptions.push(
            dialogBox.afterClosed().subscribe(() => {
                if (dialogBox.componentInstance.saved) {
                    this.reload.emit(true);
                }
            })
        );
    }

    showAttachments(information: Profile) {
        // POST: shows the attachment menu dialog
        if (information.ProfileAttachments == null || information.ProfileAttachments == undefined) {
            information.ProfileAttachments = [];
        }
        this.dialog.open(AttachmentsMenuDialogComponent, {
            data: {
                attachments: information.ProfileAttachments,
                userCanAdd: this.userCanEdit,
                profile: information
            },
            disableClose: true
        });
    }

    moveContentRowUp(node: ProfileNode) {
        // PRE: a node to move
        // POST: moves the node one level up
        const currentSequence = node.data.sequence;
        let lowerSequences;
        let nextContent;
        if (node.data.ParentID == null || node.data.ParentID == -1) {
            lowerSequences = this.profiles.filter(info => info.ParentID == -1 && info.TopicId == node.data.TopicId && info.sequence < node.data.sequence);
            if (!this.displayInactive) {
                lowerSequences = lowerSequences.filter(info => info.Status == 'A');
            }
        }
        else {
            lowerSequences = this.profiles.filter(info => info.ParentID == node.data.ParentID && info.sequence < node.data.sequence);
            if (!this.displayInactive) {
                lowerSequences = lowerSequences.filter(info => info.Status == 'A');
            }
        }
        nextContent = lowerSequences.find(sequence => sequence.sequence == Math.max.apply(null, lowerSequences.map(seq => seq.sequence)));
        if (nextContent) {
            node.data.newSequence = nextContent.sequence;
            nextContent.newSequence = currentSequence;
        }
        this.unsavedChanges.emit(true);
        this.assembleDisplayData();
    }

    moveContentRowDown(node: ProfileNode) {
        // PRE: a node to move
        // POST: moves the node one level down
        const currentSequence = node.data.sequence;
        let higherSequences;
        let nextContent;
        if (node.data.ParentID == null || node.data.ParentID == -1) {
            higherSequences = this.profiles.filter(info => info.ParentID == -1 && info.TopicId == node.data.TopicId && info.sequence > node.data.sequence);
            if (!this.displayInactive) {
                higherSequences = higherSequences.filter(info => info.Status == 'A');
            }
        }
        else {
            higherSequences = this.profiles.filter(info => info.ParentID == node.data.ParentID && info.sequence > node.data.sequence);
            if (this.displayInactive == false) {
                higherSequences = higherSequences.filter(info => info.Status == 'A');
            }
        }
        nextContent = higherSequences.find(sequence => sequence.sequence == Math.min.apply(null, higherSequences.map(seq => seq.sequence)));
        if (nextContent) {
            node.data.newSequence = nextContent.sequence;
            nextContent.newSequence = currentSequence;
        }
        this.unsavedChanges.emit(true);
        this.assembleDisplayData();
    }

    moveRowUp(topic: Topic) {
        // PRE: a topic to move
        // POST: moves the topic one level up
        const currentSequence = topic.sequence;
        let lowerSequences = this.profiles.filter(info => info.ProfileContentId == -2 && info.ParentID == -2 && info.sequence < topic.sequence);
        if (!this.displayInactive) {
            lowerSequences = lowerSequences.filter(sequence => sequence.Status == 'A');
        }
        const nextContent = lowerSequences.find(sequence => sequence.sequence == Math.max.apply(null, lowerSequences.map(seq => seq.sequence)));
        if (nextContent) {
            topic.topLevelNode.data.newSequence = nextContent.sequence;
            nextContent.newSequence = currentSequence;
        }
        this.unsavedChanges.emit(true);
        this.assembleDisplayData();
    }

    moveRowDown(topic: Topic) {
        // PRE: a topic to move
        // POST: moves the topic one level dow
        const currentSequence = topic.sequence;
        let higherSequences = this.profiles.filter(info => info.ProfileContentId == -2 && info.ParentID == -2 && info.sequence > topic.sequence);
        if (!this.displayInactive) {
            higherSequences = higherSequences.filter(sequence => sequence.Status == 'A');
        }
        const nextContent = higherSequences.find(sequence => sequence.sequence == Math.min.apply(null, higherSequences.map(seq => seq.sequence)));
        if (nextContent) {
            topic.topLevelNode.data.newSequence = nextContent.sequence;
            nextContent.newSequence = currentSequence;
        }
        this.unsavedChanges.emit(true);
        this.assembleDisplayData();
    }

    order() {
        // POST: organizes the data in order of sequence
        this.topics.sort((topicA, topicB) => {
            if (topicA.sequence < topicB.sequence) {
                return -1;
            } else if (topicA.sequence > topicB.sequence) {
                return 1;
            } else {
                return 0;
            }
        });
        for (let i = 0; i < this.topics.length; i++) {
            const topic = this.topics[i];
            if (i == 0) {
                topic.first = true;
            } else {
                topic.first = false;
            }
            if (i == this.topics.length - 1) {
                topic.last = true;
            } else {
                topic.last = false;
            }
            topic.topLevelNode.sortChildren();
        }
    }

    assembleDisplayData() {
        // PRE: the profiles must be set
        // POST: assembles the data for display

        // Don't do anything if there's no profiles
        if (!this.profiles) {
            return;
        }

        // Capturing which information is currently open
        if (this.tableData && this.topics) {
            this.openInformation = [];
            this.topics.forEach(topic => {
                if (this.tableData.checkExpanded(topic)) {
                    this.openInformation.push(topic.topLevelNode.data);
                }
                this.openInformation.push.apply(this.openInformation, topic.topLevelNode.getOpenedNodes());
            });
        }

        // Creating list of topics
        const topics: {[key: number]: Topic} = {};
        this.topics = [];

        // Filtering out inactive if we're not displaying them
        let shownInformation = this.profiles;
        if (!this.displayInactive) {
            shownInformation = shownInformation.filter(info => info.Status == 'A');
        }

        // Determining which columns we should show
        this.allColumns.forEach(columns => {
            if (this.userCanEdit) {
                if (!columns.includes('actions')) {
                    columns.push('actions');    // Actions always go on the end
                }
            } else if (columns.includes('actions')) {
                columns.splice(columns.indexOf('actions'), 1);
            }
            if (shownInformation.find(info => info.Status == 'I')) {
                if (!columns.includes('inactive')) {
                    if (columns.includes('actions')) {
                        columns.splice(columns.length - 1, 0, 'inactive');   // Inactive always goes before action if column contains action
                    }
                    else {
                        columns.splice(columns.length, 0, 'inactive');//  Inactive always goes at last
                    }
                }
            } else if (columns.includes('inactive')) {
                columns.splice(columns.indexOf('inactive'), 1);
            }
            if (this.isViewing) {
                if (columns.includes('attachments')) {
                    columns.splice(columns.indexOf('attachments'), 1);
                }
                if (columns.includes('start date')) {
                    columns.splice(columns.indexOf('start date'), 1);
                }
                if (columns.includes('end date')) {
                    columns.splice(columns.indexOf('end date'), 1);
                }
            } else if (columns.includes('attachments link')) {
                columns.splice(columns.indexOf('attachments link'), 1);
            }
        });

        // Determining the size of the profile tables
        this.profileTableWidth = 0;
        this.profileColumns.forEach(column => {
            if (this.columnWidths[column]) {
                this.profileTableWidth += this.columnWidths[column];
            }
        });

        // Assembling topics
        // NOTE: because of the way the hierarchy works, a parent inactive will prevent any active children from showing
        shownInformation.forEach(profile => {

            // Creating the node
            const node = new ProfileNode();
            node.data = profile;

            // Assigning it to a topic
            if (!topics[profile.TopicId]) {
                topics[profile.TopicId] = new Topic();
            }
            const topic = topics[profile.TopicId];
            node.topic = topic;
            topic.children.push(node);
            if (node.data.ParentID == -2) {
                topic.topLevelNode = node;
                topic.subLevelNodes.forEach(subNode => {
                    node.children.push(subNode);
                    subNode.parent = node;
                });
            } else if (node.data.ParentID == -1) {
                topic.subLevelNodes.push(node);
                if (topic.topLevelNode) {
                    topic.topLevelNode.children.push(node);
                    node.parent = topic.topLevelNode;
                }
            }

            // Scanning for parents and children
            topic.children.forEach(topicNode => {
                if  (topicNode.data.ParentID >= 0 && topicNode.data.ParentID == node.data.ProfileContentId) {
                    node.children.push(topicNode);
                    topicNode.parent = node;
                } else if (topicNode.data.ProfileContentId >= 0 && topicNode.data.ProfileContentId == node.data.ParentID) {
                    topicNode.children.push(node);
                    node.parent = topicNode;
                }
            });

            // Putting this in the persistent list
            // NOTE: we only want to add the topic if it has a top-level node or the component will break
            if (topic.topLevelNode != null && !this.topics.includes(topic)) {
                this.topics.push(topic);
            }
        });

        // Ordering the data by sequence
        this.order();

        // Enforcing the date (we have to do this after to deactivate children)
        if (this.enforceDates) {
            for (let i = 0; i < this.topics.length; i++) {

                // Removing the topic altogether if it's outside the date range
                if (!(this.topics[i].topLevelNode.data instanceof Profile)) {
                    this.topics[i].topLevelNode.data = System.convert<Profile>(this.topics[i].topLevelNode.data, new Profile());
                }
                if (this.topics[i].topLevelNode.data.outsideOfDateRange()) {
                    this.topics.splice(i, 1);
                    i--;
                }

                // Removing children
                else {
                    const removeChildren = (node: ProfileNode, parent: ProfileNode, index: number) => {
                        if (!(node.data instanceof Profile)) {
                            node.data = System.convert<Profile>(node.data, new Profile());
                        }
                        if (node.data.outsideOfDateRange()) {
                            if (parent) {
                                parent.children.splice(index, 1);
                            }
                            const topicIndex = this.topics[i].subLevelNodes.indexOf(node);
                            if (topicIndex >= 0) {
                                this.topics[i].subLevelNodes.splice(topicIndex, 1);
                            }
                        } else {
                            for (let j = 0; j < node.children.length; j++) {
                                removeChildren(node.children[j], node, j);
                            }
                        }
                    };
                    removeChildren(this.topics[i].topLevelNode, null, 0);
                }
            }
        }

        // Ensuring table data
        this.topics.forEach(topic => {
            topic.children.forEach(topicNode => {
                topicNode.subTableData = new TylerMatTableDataSource(topicNode.children);
            });
        });
        this.tableData = new TylerMatTableDataSource(this.topics);

        // Opening information that was previously opened
        if (this.openInformation) {
            this.topics.forEach(topic => {
                if (this.openInformation.find(info => info.RowId == topic.topLevelNode.data.RowId)) {
                    this.tableData.toggleExpanded(topic);
                }
                topic.topLevelNode.openNodes(this.openInformation);
            });
        }

        // Tracking changes to prevent errors
        this.changeDetector.detectChanges();
    }
}
